/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "HTTP API", description = "Hightouch's HTTP API endpoint for collecting events", lastUpdatedDate = new Date(1790640000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "HTTP API",
        description: "Hightouch's HTTP API endpoint for collecting events",
        lastUpdatedDate: "Tue Sep 29 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "HTTP API Source",
          "depth": 0,
          "id": "http-api-source"
        }, {
          "value": "Headers",
          "depth": 0,
          "id": "headers"
        }, {
          "value": "Authentication",
          "depth": 1,
          "id": "authentication"
        }, {
          "value": "Content-Type",
          "depth": 0,
          "id": "content-type"
        }, {
          "value": "Identify",
          "depth": 0,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 0,
          "id": "track"
        }, {
          "value": "Page events",
          "depth": 0,
          "id": "page-events"
        }, {
          "value": "Screen events",
          "depth": 0,
          "id": "screen-events"
        }, {
          "value": "Group events",
          "depth": 0,
          "id": "group-events"
        }, {
          "value": "Batch",
          "depth": 0,
          "id": "batch"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      code: "code",
      strong: "strong",
      em: "em",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "http-api-source",
        children: "HTTP API Source"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's HTTP Tracking API enables you to record data from any website or application."
      }), "\n", _jsx(_components.h2, {
        id: "headers",
        children: "Headers"
      }), "\n", _jsx(_components.h3, {
        id: "authentication",
        children: "Authentication"
      }), "\n", _jsx(_components.p, {
        children: "To authenticate Hightouch's tracking API, send your projects Write Key along with a request. Authentication uses HTTP Basic Auth."
      }), "\n", _jsxs(_components.p, {
        children: ["To do this, you should include an Authorization header that includes ", _jsx(_components.code, {
          children: "Basic"
        }), " followed by your base64 encoded write key. This looks like this:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "Authorization: Basic YmJjYmU434YTNmRlNWRkYjM3MDUzMDRlMzN2JjM2NiY2RjYWJmZTA5YTA3ZGYxWNkNjQ5Zg=="
        })
      }), "\n", _jsx(_components.h2, {
        id: "content-type",
        children: "Content-Type"
      }), "\n", _jsx(_components.p, {
        children: "Ensure your content-type header is set to 'application/json'"
      }), "\n", _jsx(_components.h2, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Identify events"
        }), " capture data about ", _jsx(_components.em, {
          children: "who"
        }), " your users are. This user context is vital for understanding and targeting your users. For example, you might use the collected identifiers in followup email campaigns, to segment your users by location, or to link anonymous and authenticated events."]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["Example ", _jsx(_components.code, {
            children: "identify"
          }), " call:"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "POST https://us-east-1.hightouch-events.com/v1/identify\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-perl",
          children: ["{\n\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"type\""
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"identify\""
          }), ",\n\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"messageId\""
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"111e984d-c93c-444c-b29c-f499a117c500\""
          }), ",\n\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"traits\""
          }), ":  {\n\t\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kevin@hightouch.io\""
          }), "\n\t},\n\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"anonymousId\""
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), ",\n\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"userId\""
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"originalTimestamp\""
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.063Z\""
          }), ",\n\t", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sentAt\""
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.064Z\""
          }), "\n}\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Track events"
        }), " capture data on ", _jsx(_components.em, {
          children: "what"
        }), " the user is doing. Track events can capture a broad range of actions, such as clicking a button, completing a purchase, or creating a new account."]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["Example ", _jsx(_components.code, {
            children: "track"
          }), " call"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "POST https://us-east-1.hightouch-events.com/v1/track\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"7b1e2872-4ebb-4779-b9ad-e7aa1a8eab82\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Purchase completed\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n\t\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"cart_total\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-number",
            children: "24.99"
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:12:00.584Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), "  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:12:00.585Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "page-events",
        children: "Page events"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Page events"
        }), " track the webpages users interact with. In most situations, you should automatically call the page load event when a page is loaded."]
      }), "\n", _jsx(_components.p, {
        children: "For single page apps, you should simulate page events in your routing layer."
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["Example ", _jsx(_components.code, {
            children: "page"
          }), " call"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "POST https://us-east-1.hightouch-events.com/v1/page\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"page\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"bb9fcbbd-9e19-4bfd-a726-7b1e7e44f8ba\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"title\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Home\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.061Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.063Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "screen-events",
        children: "Screen events"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Screen events"
        }), " are the equivalent of page views for ", _jsx(_components.em, {
          children: "mobile apps"
        }), ". Like page events, you should automatically call the screen event when a mobile screen view is loaded."]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["Example ", _jsx(_components.code, {
            children: "screen"
          }), " call"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "POST https://us-east-1.hightouch-events.com/v1/screen\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"screen\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"bb9fcbbd-9e19-4bfd-a726-7b1e7e44f8ba\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"title\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Home\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.061Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.063Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "group-events",
        children: "Group events"
      }), "\n", _jsx(_components.p, {
        children: "Group events let you associate users with a larger group. For example, a user may be part of an organization, or family."
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "traits"
        }), " field is optional, and can include information on traits on the group. For example, if linking a user to a company, you may include traits on the company such as the company size or location."]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["Example ", _jsx(_components.code, {
            children: "group"
          }), " call"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "POST https://us-east-1.hightouch-events.com/v1/group\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"group\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"d87596da-5f1e-40f4-a28a-7099290548b7\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"groupId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"456\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"traits\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"company_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Hightouch\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:46:23.098Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:46:23.099Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "batch",
        children: "Batch"
      }), "\n", _jsxs(_components.p, {
        children: ["Use the ", _jsx(_components.strong, {
          children: "batch"
        }), " endpoint to submit multiple events to the collector in a single request. Each item in ", _jsx(_components.code, {
          children: "batch"
        }), " is a full event payload (", _jsx(_components.code, {
          children: "identify"
        }), ", ", _jsx(_components.code, {
          children: "track"
        }), ", ", _jsx(_components.code, {
          children: "page"
        }), ", ", _jsx(_components.code, {
          children: "screen"
        }), ", ", _jsx(_components.code, {
          children: "group"
        }), ", or ", _jsx(_components.code, {
          children: "alias"
        }), ") and must include a ", _jsx(_components.code, {
          children: "type"
        }), " field."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each request can contain at most ", _jsx(_components.strong, {
          children: "10,000 events"
        }), " and must fit within ", _jsx(_components.strong, {
          children: "1 MiB (1,048,576 bytes)"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Requests with more than 10,000 events return HTTP ", _jsx(_components.code, {
          children: "400"
        }), ". The collector rejects the entire request without accepting any events. Split larger batches into smaller requests."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally set top-level ", _jsx(_components.code, {
          children: "context"
        }), " and ", _jsx(_components.code, {
          children: "sentAt"
        }), " values. Hightouch applies those defaults to every event in the batch."]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["Example ", _jsx(_components.code, {
            children: "batch"
          }), " call"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "POST https://us-east-1.hightouch-events.com/v1/batch\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-perl",
          children: ["{\n  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"batch\""
          }), ": [\n    {\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"type\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"identify\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"messageId\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"111e984d-c93c-444c-b29c-f499a117c500\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"traits\""
          }), ": {\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kevin@hightouch.io\""
          }), "\n      },\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"anonymousId\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"userId\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"originalTimestamp\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.063Z\""
          }), "\n    },\n    {\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"type\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"messageId\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"7b1e2872-4ebb-4779-b9ad-e7aa1a8eab82\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"anonymousId\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"userId\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"event\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Purchase completed\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"properties\""
          }), ": {\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cart_total\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "24.99"
          }), "\n      },\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"originalTimestamp\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:12:00.584Z\""
          }), "\n    }\n  ],\n  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"context\""
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"library\""
          }), ": {\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"my-server\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"version\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"1.0.0\""
          }), "\n    }\n  },\n  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sentAt\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:12:00.585Z\""
          }), "\n}\n"]
        })
      })]
    });
  }
}
export default MDXContent;
